import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import MainLayout from 'layout/MainLayout'
import {
  BrandingPage,
  CampaignPage,
  AgenciesPage,
  AgencyDetailsPage,
  MarketingPage,
  StaffLicensePage,
  StaffMemberPage,
  ManageCampaign,
  ContractPage,
  PropertyDetailsPage,
  TenancyInformationPage,
  VendorDetailsPage,
  Permissions,
  IntegrationsPage,
  StaffReporting,
  CampaignMarketing,
  CampaignMarketingDetails,
  CampaignsDashboard,
  Contracts,
  ContractsDashboard,
  ListingAgreement,
  ContractSetting,
  Partner,
  Network,
  ListingsPage,
  PartnershipPage,
  Submissions,
  ContractFiles,
  ActivityLogView,
  BuyersInformation,
  BuyersSolicitor,
  BuyersAgent,
  TasksPage,
  SaleInformation,
  Conditions,
  Settlement,
  Deposits,
  SpecialConditions,
  Finance,
  Commission,
  Referral,
  Vpa,
  ServiceFee,
  FeatureToggles,
} from 'pages'
import { PageNotFoundPage, UnexpectedErrorPage } from 'pages/errorPages'
import { FormType } from '@urbanx/agx.campaigns.client'
import { MenuTitles, Pages } from './menu'
import ProtectedRoute from 'layout/ProtectedRoute'
import Discrepancies from 'pages/discrepancies/discrepancies'
import { StaffRole } from '@urbanx/agx.auth.client'
import CampaignOverview from 'pages/campaigns/components/campaignOverview/CampaignOverview'
import CampaignTasks from 'pages/campaigns/components/campaignTasks/CampaignTasks'
import TeamPageLayout from 'pages/team/TeamPageLayout'
import TeamMembersPage from 'pages/team/TeamMembersPage/TeamMembersPage'
import AddOrEditTeamMemberPage from 'pages/team/AddOrEditTeamMemberPage/AddOrEditTeamMemberPage'
import ManageTeamsPage from 'pages/team/ManageTeamsPage/ManageTeamsPage'
import AddOrEditTeamPage from 'pages/team/AddOrEditTeamPage/AddOrEditTeamPage'
import ManageUrbanXTeamPage from 'pages/team/ManageUrbanXTeamPage/ManageUrbanXTeamPage'
import AgencyPage from 'pages/agency/AgencyPage'
import ContractTasks from 'pages/contracts/components/ContractTasks'
import SupportPage from 'pages/support/SupportPage'
import PoisonQueues from 'pages/support/PoisonQueues/PoisonQueues'
import PoisonQueue from 'pages/support/PoisonQueue/PoisonQueue'
import PoisonQueueMessage from 'pages/support/PoisonQueue/PoisonQueueMessage'
import ReportingPage from 'pages/reportingPage/reportingPage'
import PartnersPage from 'pages/reportingPage/pages/PartnersPage'
import PartnerPage from 'pages/reportingPage/pages/pages/PartnerPage'
import IndividualsPage from 'pages/reportingPage/pages/pages/IndividualsPage'
import LeaderboardPage from 'pages/reportingPage/pages/pages/LeaderboardPage'
import TasksDashboardPage from 'pages/tasksDashboard/TasksDashboardPage'
import EditStaffMember from 'pages/staff/EditStaffMember'
import CreateStaffMember from 'pages/staff/CreateStaffMember'
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles'

// Component to handle conditional dashboard routing
const DashboardPage = () => {
  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles()
  const canViewTasks = doesUserHaveRequiredRoles([
    StaffRole.WorkflowContributor,
  ])

  return canViewTasks ? <TasksPage /> : <AgenciesPage />
}

const router = createBrowserRouter([
  {
    path: Pages.DASHBOARD,
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.TASKS_DASHBOARD,
        element: (
          <ProtectedRoute
            element={<TasksDashboardPage />}
            requiredRoles={[StaffRole.WorkflowContributor]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.CAMPAIGNS,
        element: <CampaignsDashboard />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.CONTRACTS,
        element: <ContractsDashboard />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.AGENCIES,
        element: <AgenciesPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: `${Pages.AGENCIES}/:agencyId`,
        element: <AgencyPage />,
        errorElement: <UnexpectedErrorPage />,
        children: [
          {
            path: Pages.CAMPAIGNS,
            element: <CampaignPage />,
            errorElement: <UnexpectedErrorPage />,
          },

          {
            path: `${Pages.CAMPAIGNS}/:campaignId`,
            element: <Outlet />,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                path: Pages.CAMPAIGN_OVERVIEW,
                element: <CampaignOverview />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.CAMPAIGN_TASKS,
                element: <CampaignTasks />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.PROPERTY_INFORMATION,
                element: <ManageCampaign formType={FormType.BlankContract} />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.PROPERTY_INFORMATION_SECTION,
                element: <Outlet />,
                errorElement: <UnexpectedErrorPage />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="property-details" replace />,
                  },
                  {
                    path: 'property-details',
                    element: <PropertyDetailsPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'vendor-details',
                    element: <VendorDetailsPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'contract',
                    element: <ContractPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'tenancy-information',
                    element: <TenancyInformationPage />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                ],
              },
              {
                path: Pages.FINANCE,
                element: <Finance />,
                errorElement: <UnexpectedErrorPage />,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={Pages.COMMISSION_COMMISSION}
                        replace
                        state={{ activeTab: MenuTitles.FINANCE }}
                      />
                    ),
                  },
                  {
                    path: Pages.COMMISSION_COMMISSION,
                    element: <Commission />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.COMMISSION_SERVICE_FEE,
                    element: <ServiceFee />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.COMMISSION_REFERRAL,
                    element: <Referral />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: 'vpa',
                    element: <Vpa />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                ],
              },
              {
                path: Pages.CAMPAIGN_MARKETING,
                element: <CampaignMarketing />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.CONTRACTS,
                element: <Contracts />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.ACTIVITY,
                element: <ActivityLogView />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.FILES,
                element: <Submissions />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.PERMISSIONS,
                element: <Permissions />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.CAMPAIGN_MARKETING}/:marketingPackageId`,
                element: <CampaignMarketingDetails />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.CONTRACTS}/:contractId`,
                element: <Outlet />,
                errorElement: <UnexpectedErrorPage />,
                children: [
                  {
                    path: Pages.CONTRACT_TASKS,
                    element: <ContractTasks />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_INFORMATION,
                    element: <BuyersInformation />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_SOLICITOR,
                    element: <BuyersSolicitor />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.BUYERS_AGENT,
                    element: <BuyersAgent />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.SALE_INFORMATION,
                    element: <SaleInformation />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.CONDITIONS,
                    element: <Conditions />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.SPECIAL_CONDITIONS,
                    element: <SpecialConditions />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.SETTLEMENT,
                    element: <Settlement />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.DEPOSITS,
                    element: <Deposits />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                  {
                    path: Pages.FILES,
                    element: <ContractFiles />,
                    errorElement: <UnexpectedErrorPage />,
                  },
                ],
              },
            ],
          },
          {
            path: Pages.WEBSITE_LISTINGS,
            element: <ListingsPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.MARKETING,
            element: <MarketingPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.TEAMS,
            element: <TeamPageLayout />,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                index: true,
                element: <Navigate to={Pages.TEAMS_MEMBERS} replace />,
              },
              {
                path: Pages.TEAMS_MEMBERS,
                element: <TeamMembersPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MEMBERS}/add`,
                element: <AddOrEditTeamMemberPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MEMBERS}/edit/:agentId`,
                element: <AddOrEditTeamMemberPage isEdit />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.TEAMS_MANAGE_TEAMS,
                element: <ManageTeamsPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MANAGE_TEAMS}/add`,
                element: <AddOrEditTeamPage />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: `${Pages.TEAMS_MANAGE_TEAMS}/edit/:teamId`,
                element: <AddOrEditTeamPage isEdit />,
                errorElement: <UnexpectedErrorPage />,
              },
              {
                path: Pages.TEAMS_URBANX_TEAM,
                element: <ManageUrbanXTeamPage />,
                errorElement: <UnexpectedErrorPage />,
              },
            ],
          },
          {
            path: `${Pages.PARTNERSHIP}/*`,
            element: (
              <ProtectedRoute
                element={<PartnershipPage />}
                requiredRoles={[
                  StaffRole.BusinessManager,
                  StaffRole.SuperAdmin,
                  StaffRole.Executive,
                ]}
              />
            ),
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.AGENCY_DETAILS,
            element: <AgencyDetailsPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.BRANDING,
            element: <BrandingPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.INTEGRATIONS,
            element: <IntegrationsPage />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.LISTING_AGREEMENT,
            element: <ListingAgreement />,
            errorElement: <UnexpectedErrorPage />,
          },
          {
            path: Pages.CONTRACT_SETTING,
            element: <ContractSetting />,
            errorElement: <UnexpectedErrorPage />,
          },
        ],
      },

      {
        path: Pages.STAFF,
        element: <StaffMemberPage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.EDIT_STAFF,
        element: <Navigate to={Pages.STAFF} replace />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: `${Pages.EDIT_STAFF}/:userId`,
        element: <EditStaffMember />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.CREATE_STAFF,
        element: <CreateStaffMember />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.STAFF_LICENSE,
        element: <StaffLicensePage />,
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: 'executive-report',
        element: (
          <ProtectedRoute
            element={<StaffReporting />}
            requiredRoles={[StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: 'network',
        element: (
          <ProtectedRoute
            element={<Network />}
            requiredRoles={[StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: 'discrepancies',
        element: (
          <ProtectedRoute
            element={<Discrepancies />}
            requiredRoles={[StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.REPORTING_PAGE,
        element: (
          <ProtectedRoute
            element={<ReportingPage />}
            requiredRoles={[
              StaffRole.BusinessManager,
              StaffRole.SuperAdmin,
              StaffRole.Executive,
            ]}
          />
        ),
        children: [
          {
            index: true,
            element: <Navigate to={Pages.REPORTING_PARTNERS} replace />,
          },
          {
            path: Pages.REPORTING_PARTNERS,
            element: <PartnersPage />,
            children: [
              {
                index: true,
                element: (
                  <Navigate to={Pages.REPORTING_PARTNERS_PARTNER} replace />
                ),
              },
              {
                path: Pages.REPORTING_PARTNERS_PARTNER,
                element: <PartnerPage />,
              },
              {
                path: `${Pages.REPORTING_PARTNERS_PARTNER}/:agencyId`,
                element: <Partner />,
              },
              {
                path: Pages.REPORTING_PARTNERS_INDIVIDUALS,
                element: <IndividualsPage />,
              },
              {
                path: `${Pages.REPORTING_PARTNERS_INDIVIDUALS}/:agentId`,
                element: <Partner individual />,
              },
              {
                path: Pages.REPORTING_PARTNERS_LEADERBOARD,
                element: <LeaderboardPage />,
              },
            ],
          },
        ],
      },
      {
        path: Pages.FEATURE_TOGGLES,
        element: (
          <ProtectedRoute
            element={<FeatureToggles />}
            requiredRoles={[StaffRole.SuperAdmin, StaffRole.Executive]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
      },
      {
        path: Pages.SUPPORT,
        element: (
          <ProtectedRoute
            element={<SupportPage />}
            requiredRoles={[StaffRole.SuperAdmin]}
          />
        ),
        errorElement: <UnexpectedErrorPage />,
        children: [
          {
            index: true,
            element: <Navigate to={Pages.SUPPORT_QUEUES} replace />,
          },
          {
            path: Pages.SUPPORT_QUEUES,
            errorElement: <UnexpectedErrorPage />,
            children: [
              {
                index: true,
                element: <PoisonQueues />,
              },
              {
                path: ':service/:queueName',
                children: [
                  {
                    index: true,
                    element: <PoisonQueue />,
                  },
                  {
                    path: ':messageId',
                    element: <PoisonQueueMessage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <PageNotFoundPage />,
        errorElement: <UnexpectedErrorPage />,
      },
    ],
  },
])

export default router
